import {
  MealPlannerBannerInput,
  MealPlannerBannerRow,
  MealPlannerBannerMutation,
  MealPlannerCategoryRow,
  MealPlannerFilters,
  MealPlannerPage,
  MealPlannerRecipeDetail,
  MealPlannerRecipeRow,
  MealPlannerStatus,
} from '@/Interfaces/I_mealPlanner';
import { customAxios } from '../index';
import { uploadPostCollectionImageUrl } from './Media';

const BASE_URL = '/hktv_koc/cms/meal-planner';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isBannerRow = (value: unknown) => {
  if (!isRecord(value) || !isRecord(value.banner)) return false;
  const { banner } = value;
  return (
    typeof value.imageUrl === 'string' &&
    typeof banner.bannerId === 'string' &&
    typeof banner.imageUrl === 'string' &&
    typeof banner.redirectUrl === 'string' &&
    Number.isInteger(banner.seq) &&
    typeof banner.disabled === 'boolean'
  );
};

// CMS returns raw payloads. Auth failures may still use HTTP 200 envelopes.
const validateBannerResponse = <T extends { data: unknown }>(
  response: T,
  valid: (data: unknown) => boolean,
): T => {
  if (!valid(response.data)) throw new Error('Invalid banner response or authentication required');
  return response;
};

const isBannerMutation = (data: unknown) =>
  isRecord(data) && typeof data.bannerId === 'string' && typeof data.cacheRefreshed === 'boolean';

export const getMealPlannerBanners = (signal?: AbortSignal) =>
  customAxios
    .get<{ banners: MealPlannerBannerRow[] }>(`${BASE_URL}/banners`, { signal })
    .then((response) =>
      validateBannerResponse(
        response,
        (data) => isRecord(data) && Array.isArray(data.banners) && data.banners.every(isBannerRow),
      ),
    );

export const createMealPlannerBanner = (body: MealPlannerBannerInput) =>
  customAxios
    .post<MealPlannerBannerMutation>(`${BASE_URL}/banners`, body)
    .then((response) => validateBannerResponse(response, isBannerMutation));

export const updateMealPlannerBanner = (id: string, body: MealPlannerBannerInput) =>
  customAxios
    .put<MealPlannerBannerMutation>(`${BASE_URL}/banners/${encodeURIComponent(id)}`, body)
    .then((response) => validateBannerResponse(response, isBannerMutation));

export const deleteMealPlannerBanner = (id: string) =>
  customAxios
    .delete<MealPlannerBannerMutation>(`${BASE_URL}/banners/${encodeURIComponent(id)}`)
    .then((response) => validateBannerResponse(response, isBannerMutation));

export const refreshMealPlannerBanners = () =>
  customAxios.post(`${BASE_URL}/banners/refresh-cache`).then((response) => {
    if (response.status !== 204) throw new Error('Banner cache refresh was not confirmed');
    return response;
  });

export const uploadMealPlannerBanner = (file: File) => {
  const body = new FormData();
  body.append('images', file);
  body.append('systemName', 'koc');
  body.append('functionName', 'meal-planner');
  return customAxios
    .post<{ imageUrl: string }[]>(uploadPostCollectionImageUrl, body)
    .then((response) =>
      validateBannerResponse(
        response,
        (data) =>
          Array.isArray(data) &&
          data.length > 0 &&
          data.every(
            (image) =>
              isRecord(image) &&
              typeof image.imageUrl === 'string' &&
              image.imageUrl.startsWith('/') &&
              !image.imageUrl.startsWith('//'),
          ),
      ),
    );
};

export const getMealPlannerCategories = (params: MealPlannerFilters, signal?: AbortSignal) =>
  customAxios.get<MealPlannerPage<MealPlannerCategoryRow>>(`${BASE_URL}/categories`, {
    params,
    signal,
  });

export const getMealPlannerCategory = (id: string, signal?: AbortSignal) =>
  customAxios.get<MealPlannerCategoryRow>(`${BASE_URL}/categories/${encodeURIComponent(id)}`, {
    signal,
  });

export const getMealPlannerRecipes = (params: MealPlannerFilters, signal?: AbortSignal) =>
  customAxios.get<MealPlannerPage<MealPlannerRecipeRow>>(`${BASE_URL}/recipes`, { params, signal });

export const getMealPlannerRecipe = (id: string, signal?: AbortSignal) =>
  customAxios.get<{ recipe: MealPlannerRecipeDetail }>(
    `${BASE_URL}/recipes/${encodeURIComponent(id)}`,
    { signal },
  );

export const setMealPlannerStatus = (
  kind: 'categories' | 'recipes',
  id: string,
  disabled: boolean,
) =>
  customAxios.patch<MealPlannerStatus>(`${BASE_URL}/${kind}/${encodeURIComponent(id)}/status`, {
    disabled,
  });
